import { useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiClockOutline,
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiTruckDeliveryOutline,
  mdiPackageVariantClosed,
  mdiArrowDown,
  mdiArrowUp,
} from "@mdi/js";
import { useHistory } from "react-router-dom";
import { urls } from "../../../../utils/urls";
import { WarehouseInwardModel } from "../model/warehouse_inward_models";
import {
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import { formatAmountWithCommas } from "../../content_panel/utils/helpers";
import { getOutwardSourceLabel, getStatusStyle } from "../warehouse_helper";
import "../inward-styles.css";
import { fetchWarehouseDashboardSummary } from "../warehouse_module_repo";

const statusClassMap: Record<string, string> = {
  received: "status-received",
  partial: "status-partial",
  pending: "status-pending",
  disputed: "status-disputed",
};

export const WarehouseDashboard = () => {
  const [dashboardData, setDashboardData] = useState<{
    completedInward: number;
    partialReceived: number;
    pendingInward: number;
    delivered: number;
    outwardShipped: number;
    pendingOutward: number;
  }>({
    completedInward: 0,
    partialReceived: 0,
    pendingInward: 0,
    delivered: 0,
    outwardShipped: 0,
    pendingOutward: 0,
  });
  const navigate = useHistory();
  const [inwardData, setInwardData] = useState<WarehouseInwardModel[]>([]);
  const [outwardData, setOutwardData] = useState<WarehouseOutwardModel[]>([]);
  const [loading, setLoading] = useState(true);

  // Sample data - replace with actual API calls
  const summaryCards = [
    {
      title: "Completed Inward",
      value: dashboardData.completedInward.toString(),
      change: null,
      changeType: null,
      icon: mdiPackageVariantClosed,
      iconColor: "text-green-500",
    },
    {
      title: "Partial Received",
      value: dashboardData.partialReceived.toString(),
      change: null,
      changeType: null,
      icon: mdiAlertCircleOutline,
      iconColor: "text-blue-500",
    },
    {
      title: "Pending Inward",
      value: dashboardData.pendingInward.toString(),
      change: null,
      changeType: null,
      icon: mdiClockOutline,
      iconColor: "text-yellow-500",
    },
    {
      title: "Delivered",
      value: dashboardData.delivered.toString(),
      change: null,
      changeType: null,
      icon: mdiCheckCircleOutline,
      iconColor: "text-green-500",
    },
    {
      title: "Outward Shipped",
      value: dashboardData.outwardShipped.toString(),
      change: null,
      changeType: null,
      icon: mdiTruckDeliveryOutline,
      iconColor: "text-blue-500",
    },
    {
      title: "Pending Outward",
      value: dashboardData.pendingOutward.toString(),
      change: null,
      changeType: null,
      icon: mdiClockOutline,
      iconColor: "text-yellow-500",
    },
  ];

  useEffect(() => {
    const fetchdata = async () => {
      setLoading(true);
      try {
        const res = await fetchWarehouseDashboardSummary();

        if (res?.success) {
          const inward = res.data?.inward ?? {};
          const outward = res.data?.outward ?? {};

          setInwardData(inward.recent ?? []);
          setOutwardData(outward.recent ?? []);

          // KPI counts are computed server-side over the entire collection
          // (not just the returned rows), so the cards reflect true totals.
          const inCounts = inward.counts ?? {};
          const outCounts = outward.statusCount ?? {};

          setDashboardData({
            completedInward: inCounts.received ?? 0,
            partialReceived: inCounts.partial ?? 0,
            pendingInward: inCounts.pending ?? 0,
            delivered: outCounts["Delivered"] ?? 0,
            outwardShipped: outCounts["Dispatched"] ?? 0,
            pendingOutward: outCounts["Pending"] ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchdata();
  }, []);

  if (loading) {
    return (
      <div className="wh-mobile-shrink min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600 text-sm font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="warehouse-dashboard-screen"
      className="wh-mobile-shrink min-h-screen bg-gray-50 p-6"
    >
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="flex items-center gap-4"></div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {summaryCards.map((card, index) => (
          <div
            key={index}
            id={`warehouse-dashboard-stat-${card.title
              .toLowerCase()
              .replace(/\s+/g, "-")}`}
            className="bg-white rounded-lg shadow-sm p-5 border border-gray-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">{card.title}</p>
                <p className="text-2xl font-bold text-gray-800 mb-1">
                  {card.value}
                </p>
                {card.change && (
                  <div className="flex items-center gap-1">
                    {card.changeType === "up" ? (
                      <Icon
                        path={mdiArrowUp}
                        size={0.7}
                        className="text-green-500"
                      />
                    ) : (
                      <Icon
                        path={mdiArrowDown}
                        size={0.7}
                        className="text-red-500"
                      />
                    )}
                    <span
                      className={`text-xs ${
                        card.changeType === "up"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {card.change}
                    </span>
                  </div>
                )}
              </div>
              <Icon path={card.icon} size={1.5} className={card.iconColor} />
            </div>
          </div>
        ))}
      </div>

      {/* Middle Section */}

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Inward Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-96">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800">
              Recent Inward Orders
            </h2>
            <button
              id="warehouse-dashboard-inward-view-all-btn"
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              onClick={() => navigate.push(urls.warehouse_inward)}
            >
              View All →
            </button>
          </div>
          <div
            className="overflow-x-auto overflow-y-auto flex-1 scrollbar-hide"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    To Warehouse
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {inwardData.slice(0, 5).map((order, index) => {
                  const statusKey = order?.status.toLowerCase();
                  const statusClass = statusClassMap[statusKey] ?? "";

                  return (
                    <tr
                      key={index}
                      id={`warehouse-dashboard-inward-row-${
                        order.inward_order_id || index
                      }`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.inward_order_id}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                        {order.type}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                        {order.toWarehouse.name}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {order.totalPrice}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full border ${statusClass}`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Outward Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-96">
          <div className="p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800">
              Recent Outward Orders
            </h2>
            <button
              id="warehouse-dashboard-outward-view-all-btn"
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              onClick={() => navigate.push(urls.warehouse_panel + "/outward")}
            >
              View All →
            </button>
          </div>
          <div
            className="overflow-x-auto overflow-y-auto flex-1 scrollbar-hide"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    From Warehouse
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {outwardData.slice(0, 5).map((order, index) => (
                  <tr
                    key={index}
                    id={`warehouse-dashboard-outward-row-${
                      (order.type === "ST" ? order.id : order.order_id) ||
                      index
                    }`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.type === "ST" ? order.id : order.order_id}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {order.type}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getOutwardSourceLabel(order)}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      ₹{" "}
                      {formatAmountWithCommas(
                        order.items?.reduce(
                          (total: number, item: any) =>
                            total +
                            (item.itemPrice || 0) * (item.requestedQty || 0),
                          0,
                        ) || 0,
                      )}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border`}
                        style={getStatusStyle(
                          order.status as WarehouseOutwardStatus,
                        )}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
